/**
 * ============================================================================
 * ProgramDetailsOpenRfi — Model (shared form/lead types)
 * ============================================================================
 * Shared by the three ProgramDetailsOpenRfi variants:
 *  - components/organisms/NewBannerForm/ProgramDetailsOpenRfi.tsx
 *  - components/sections/NewBannerFormLp/ProgramDetailsOpenRfi.tsx
 *  - components/organisms/modal/ConnectModalMba/ProgramDetailsOpenRfiConnectModal.tsx
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";

export interface ProgramDetailsOpenRfiFormValues {
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  emailId: string;
  degree: string;
  program: string;
  pmiPathway: string;
  programId: any;
  isTermsAccepted: boolean;
}

export const SELECT_DEGREE = "Select Degree";
export const SELECT_PROGRAM = "Select Program";
export const SELECT_PMI_PATHWAY = "Choose Your PMI Pathway";

export const PMI_PATHWAY_OPTIONS = [
  "Project Management Program (PMP®)",
  "Certified Associate in Project Management (CAPM®)",
  "PMI Certified Professional in Managing AI (PMI-CPMAI™)",
];

/** Underlying values submitted for each PMI_PATHWAY_OPTIONS entry, by index. */
export const PMI_PATHWAY_VALUES = ["PMI-PMP", "PMI-CAPM", "PMI-CPMAI"];

export const errorAccordingToFieldName: Record<string, string> = {
  fullName: "Please enter valid full name",
  countryCode: "Please enter valid country code",
  phoneNumber: "Please enter valid phone number",
  emailId: "Please enter valid email address",
  degree: "Please select a degree",
  program: "Please select a program",
  pmiPathway: "Please select a PMI pathway",
  isTermsAccepted: "Please accept terms & conditions",
};

export const regexForInputs: Record<string, RegExp> = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  countryCode: /^\+\d{1,3}(?:-\d{1,4})?$/,
  phoneNumberIndia: /^[1-9][0-9]{9}$/,
  phoneNumberIntl: /^[1-9][0-9]{6,14}$/,
  emailId:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

export interface ProgramDetailsOpenRfiLeadForm {
  name: string;
  phone: string;
  countryCode: string;
  email: string;
  PROGRAM_ID: any;
  Custom6: string;
  Portal: "Degree";
  consent: true;
  Lead_temp?: string;
  scholarship_name?: string;
  scholarship_id?: string;
}

/**
 * Explicit RHF resolver — required because this form uses controlled inputs
 * via setValue/watch (CountryDropdown/Checkbox too). Relying only on
 * register()-in-useEffect was letting empty submits reach onValid without
 * populating formState.errors.
 */
export function createProgramDetailsOpenRfiResolver(
  isLandingPage: boolean,
  isPmi: boolean = false,
): Resolver<ProgramDetailsOpenRfiFormValues> {
  return (values) => {
    const errors: FieldErrors<ProgramDetailsOpenRfiFormValues> = {};

    if (!values.fullName || !regexForInputs.fullName.test(values.fullName)) {
      errors.fullName = {
        type: "validate",
        message: errorAccordingToFieldName.fullName,
      };
    }

    if (!values.countryCode || !regexForInputs.countryCode.test(values.countryCode)) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const phoneRegex =
      values.countryCode === "+91"
        ? regexForInputs.phoneNumberIndia
        : regexForInputs.phoneNumberIntl;
    if (!values.phoneNumber || !phoneRegex.test(String(values.phoneNumber))) {
      errors.phoneNumber = {
        type: "validate",
        message: errorAccordingToFieldName.phoneNumber,
      };
    }

    if (!values.emailId || !regexForInputs.emailId.test(values.emailId)) {
      errors.emailId = {
        type: "validate",
        message: errorAccordingToFieldName.emailId,
      };
    }

    if (values.isTermsAccepted !== true) {
      errors.isTermsAccepted = {
        type: "validate",
        message: errorAccordingToFieldName.isTermsAccepted,
      };
    }

    if (isLandingPage) {
      if (!values.degree || values.degree === SELECT_DEGREE) {
        errors.degree = {
          type: "validate",
          message: errorAccordingToFieldName.degree,
        };
      }
      if (!values.program || values.program === SELECT_PROGRAM) {
        errors.program = {
          type: "validate",
          message: errorAccordingToFieldName.program,
        };
      }
    }

    if (isPmi) {
      if (!values.pmiPathway || values.pmiPathway === SELECT_PMI_PATHWAY) {
        errors.pmiPathway = {
          type: "validate",
          message: errorAccordingToFieldName.pmiPathway,
        };
      }
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}
